import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ALL, PLATFORM_LIST_WITH_ALL } from '../../../../config/constants';
import {
  PLATFORM_COLORS,
  PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL,
  PLATFORM_ICONS
} from '../../../../config/icons';
import { useTranslate } from '@tolgee/react';
import { ButtonGroup, Dropdown, Badge } from 'react-bootstrap';
import {
  setConversationAttributes,
  updateAiAssistantUiAttribute,
  updateUseSeparateTextFields
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import toast from 'react-hot-toast';
import { useClickAway } from 'react-use';

interface AiResponseProps {
  message: any;
}

export const AiResponse: React.FC<AiResponseProps> = ({ message }) => {
  const { platformEnabled, quillEditors } = useSelector(
    (state: any) => state.lomavisCreator
  );
  const { t } = useTranslate();
  const dispatch = useDispatch();
  // Handle platform click
  const handlePlatformClick = (platform: string, response: string): void => {
    dispatch(
      setConversationAttributes({
        pendingUseText: {
          platform,
          response
        }
      }) as any
    );
    // check if the editor already has text
    const editor = quillEditors[platform] || null;
    const editorText = editor ? editor.getText() : null;
    if (editorText && editorText.trim().length > 0) {
      console.log('editorText', editorText);
      console.log('editorText.length', editorText.length);
      // show confirmation modal
      dispatch(
        updateAiAssistantUiAttribute({
          attribute: 'showUseTextConfirmation',
          value: true
        })
      );
      return;
    }

    dispatch(updateUseSeparateTextFields(platform !== ALL));

    if (editor) {
      editor.setText(response, 'user');
    }
    toast.success(t('lomavisCreator.aiAssistant.textUsed'), {
      position: 'top-right'
    });
  };

  // Handle copy text
  const handleCopyText = (): void => {
    navigator.clipboard.writeText(message.response || '');
  };
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  useClickAway(dropdownRef, () => {
    setShowDropdown(false);
  });
  return (
    <div className="w-75 mb-3">
      <div className="d-flex align-items-start">
        {/* AI Robot Icon */}
        <i
          className="fs-2x fa-duotone fa-solid fa-robot me-2"
          aria-hidden="true"
        />

        {/* Text bubble and dropdown */}
        <div>
          {/* The message text bubble */}
          <div className="bg-light p-2 rounded mb-2">
            <span style={{ whiteSpace: 'pre-line' }}>{message.response}</span>
          </div>

          {/* Dropdown button placed under (on the right) */}
          <div className="d-flex justify-content-end">
            <Dropdown
              className="d-flex align-items-center"
              as={ButtonGroup}
              show={showDropdown}
              ref={dropdownRef}
              direction="up"
            >
              {/* "Use text" as a clickable badge */}
              <span className="me-2 text-dark small fw-bold" onClick={() => {}}>
                {t('lomavisCreator.aiAssistant.useText')}:
              </span>

              <span
                className="me-2 text-dark cursor-pointer btn btn-secondary btn-sm rounded py-1 px-2 p-0 m-0"
                onClick={() => {
                  // Use text for ALL platforms
                  handlePlatformClick(ALL, message.response);
                  setShowDropdown(false);
                }}
              >
                {t('lomavisCreator.aiAssistant.allPlatforms')}
              </span>

              {/* Three-dots icon for toggling dropdown */}

              <Dropdown.Toggle
                variant="button"
                size="sm"
                className="text-dark cursor-pointer btn btn-secondary btn-sm rounded py-1 px-2 p-0 m-0"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                {t('lomavisCreator.aiAssistant.individualPlatforms')}
              </Dropdown.Toggle>

              {/* Dropdown Menu */}
              <Dropdown.Menu>
                {/* Loop through all platforms in PLATFORM_LIST_WITH_ALL */}
                {PLATFORM_LIST_WITH_ALL.map((platform) => {
                  if (platformEnabled[platform] || platform === ALL) {
                    return (
                      <Dropdown.Item
                        key={platform}
                        onClick={() => {
                          handlePlatformClick(platform, message.response);
                          setShowDropdown(false);
                        }}
                        className="d-flex align-items-center mb-1"
                      >
                        {/* Platform Icon */}
                        <i
                          className={`me-2 ${PLATFORM_ICONS[platform]} text-center w-25px`}
                          style={{
                            color: PLATFORM_COLORS[platform],
                            fontSize: 20
                          }}
                        ></i>
                        {t(PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL[platform])}
                      </Dropdown.Item>
                    );
                  }
                  return null;
                })}

                {/* Copy text option */}
                <Dropdown.Item
                  onClick={() => {
                    handleCopyText();
                    setShowDropdown(false);
                  }}
                >
                  <i
                    className="me-2 fa-solid fa-copy text-center w-25px"
                    style={{ fontSize: 20 }}
                  />
                  {t('lomavisCreator.aiAssistant.copyText')}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MyResponseProps {
  message: any;
  getAIprompt: any;
}

export const MyResponse: React.FC<MyResponseProps> = ({
  message,
  getAIprompt
}) => {
  const { t } = useTranslate();
  return (
    <div className="w-100">
      <div className="d-flex justify-content-end align-items-start">
        <div style={{ position: 'relative' }}>
          {/* Conditionally render the badge when quick_action is present */}

          <div
            className="bg-primary text-white p-2 rounded mb-2"
            style={{
              whiteSpace: 'pre-line',
              position: 'relative' // Ensures badge aligns to this div
            }}
          >
            {message.quick_action && (
              <div className={'text-white fw-bold'}>
                {t('lomavisCreator.aiAssistant.quickActionLabel')}
              </div>
            )}
            <div>
              {message.quick_action
                ? t(getAIprompt(message))
                : getAIprompt(message)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
